import React from 'react';
import ColorPicker from './ColorPicker';
import {
  OWASP_FINDINGS_COLUMN_KEYS, OWASP_FINDINGS_LABELS, OWASP_FINDINGS_DEFAULT_COLUMNS,
  OWASP_FINDINGS_UNCLASSIFIED, OWASP_FINDINGS_NONE, OWASP_FINDINGS_EMPTY,
  OWASP_FINDINGS_SEPARATOR, owaspFindingsLabels,
} from './owaspFindingsDefaults';

/**
 * The TSR OWASP Findings table's settings.
 *
 * Shared by the section panel and the Custom Section component panel, so the
 * two forms cannot drift. Every colour, size, heading and sentence the
 * renderers read is here, and the defaults shown in each control are the same
 * literals those renderers fall back to -- if one of them changes and this
 * does not, the panel starts lying about what the report will say.
 *
 * The rows themselves are not editable, and that is the point of the band:
 * they are the engagement's own findings grouped by category. What a template
 * gets to decide is what the columns are called and how the table looks.
 */
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];

function OwaspFindingsConfig({ config, update }) {
  const inp = {
    padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)', boxSizing: 'border-box',
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text)', fontFamily: 'inherit', minWidth: 0,
  };
  const lbl = { fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
  const row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  const group = {
    display: 'flex', flexDirection: 'column', gap: 7, padding: 8,
    borderRadius: 'var(--radius)', border: '1px solid var(--border)',
  };
  const cap = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.5px', color: 'var(--text-secondary)' };
  const note = { ...lbl, fontStyle: 'italic', whiteSpace: 'normal', lineHeight: 1.4 };

  const num = (key, fallback, min, max, width = 52) => (
    <input type="number" min={min} max={max} value={config[key] ?? fallback}
      onChange={e => update(key, Number(e.target.value) || fallback)}
      style={{ ...inp, width }} />
  );
  // allowNone={false}: every colour here is required, so the picker's "None"
  // chip would set null and be coerced straight back to the fallback.
  const colour = (key, fallback, title) => (
    <ColorPicker label={title} size={20} allowNone={false} value={config[key] || fallback}
      onChange={c => update(key, c || fallback)} />
  );
  const check = (key, text, on) => (
    <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={e => update(key, e.target.checked)}
        style={{ margin: 0, cursor: 'pointer' }} />
      {text}
    </label>
  );

  const labels = owaspFindingsLabels(config);
  const columns = Array.isArray(config.columns) && config.columns.length
    ? OWASP_FINDINGS_COLUMN_KEYS.filter(k => config.columns.includes(k))
    : [...OWASP_FINDINGS_DEFAULT_COLUMNS];
  const toggleColumn = (key) => {
    const picked = columns.includes(key) ? columns.filter(c => c !== key) : [...columns, key];
    const next = OWASP_FINDINGS_COLUMN_KEYS.filter(k => picked.includes(k));
    // Never leave the table with no columns -- it would render a border and
    // nothing else, which reads as a broken report rather than a setting.
    update('columns', next.length ? next : [key]);
  };
  const setLabel = (key, value) =>
    update('columnLabels', { ...(config.columnLabels || {}), [key]: value });

  // An empty filter means EVERY severity -- that is what both renderers do
  // with one -- so the boxes show all ticked when nothing is stored. The
  // toggle has to work from that same effective list: reading the stored
  // list directly meant unticking one box on an unfiltered band stored
  // ONLY that severity, which is the exact opposite of what was clicked and
  // silently dropped every other finding from the band.
  const sevFilter = Array.isArray(config.severityFilter) ? config.severityFilter : [];
  const shownSeverities = sevFilter.length
    ? SEVERITIES.filter(s => sevFilter.includes(s))
    : [...SEVERITIES];
  const toggleSeverity = (s) => {
    const next = shownSeverities.includes(s)
      ? shownSeverities.filter(x => x !== s)
      : [...shownSeverities, s];
    // Never leave none ticked: the renderers read an empty list as every
    // severity, so storing one would turn the exclusion back into its
    // opposite the moment it was saved.
    if (!next.length) return;
    const ordered = SEVERITIES.filter(x => next.includes(x));
    // All of them ticked is the same as no filter, and storing it as the
    // empty list is what keeps the band including a severity added later.
    update('severityFilter', ordered.length === SEVERITIES.length ? [] : ordered);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <input value={config.heading || ''} placeholder="Heading &mdash; blank for none"
        onChange={e => update('heading', e.target.value)} style={{ ...inp, width: '100%' }} />

      <div style={group}>
        <span style={cap}>Columns</span>
        <div style={row}>
          {OWASP_FINDINGS_COLUMN_KEYS.map(k => (
            <label key={k} style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={columns.includes(k)} onChange={() => toggleColumn(k)}
                style={{ margin: 0, cursor: 'pointer' }} />
              {OWASP_FINDINGS_LABELS[k]}
            </label>
          ))}
        </div>
        <div style={row}>
          <span style={lbl}>Headings:</span>
          {columns.map(k => (
            <input key={k} value={labels[k]} placeholder={OWASP_FINDINGS_LABELS[k]}
              aria-label={`${OWASP_FINDINGS_LABELS[k]} column heading`}
              onChange={e => setLabel(k, e.target.value)}
              style={{ ...inp, width: 110 }} />
          ))}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Header row</span>
        <div style={row}>
          <span style={lbl}>Background:</span>{colour('headerBg', '#1f3864', 'Header background')}
          <span style={lbl}>Text:</span>{colour('headerColor', '#ffffff', 'Header text')}
          <span style={lbl}>Size:</span>{num('headerSize', 9, 5, 20)}
          {check('headerBold', 'Bold', config.headerBold !== false)}
          <span style={lbl}>Borders:</span>{colour('borderColor', '#d8dfe8', 'Border colour')}
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Body</span>
        <div style={row}>
          <span style={lbl}>Size:</span>{num('bodySize', 9, 5, 20)}
          <span style={lbl}>Category:</span>{colour('categoryColor', '#1f3864', 'Category colour')}
          <span style={lbl}>Findings:</span>{colour('findingsColor', '#1f3864', 'Findings colour')}
          <span style={lbl}>Count:</span>{colour('countColor', '#1f3864', 'Count colour')}
        </div>
        <div style={row}>
          <span style={lbl}>Category width:</span>{num('categoryWidth', 3000, 600, 7000, 66)}
          <span style={lbl}>Count width:</span>{num('countWidth', 900, 400, 3000, 66)}
          <span style={lbl}>Count aligned:</span>
          <select value={config.countAlign || 'left'} onChange={e => update('countAlign', e.target.value)}
            style={{ ...inp, width: 86 }}>
            <option value="left">Left</option>
            <option value="center">Centre</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Which rows</span>
        <div style={row}>
          {check('showUnclassified', 'Row for findings with no category', config.showUnclassified !== false)}
          <input value={config.unclassifiedText ?? OWASP_FINDINGS_UNCLASSIFIED}
            placeholder={OWASP_FINDINGS_UNCLASSIFIED}
            aria-label="Unclassified row label"
            onChange={e => update('unclassifiedText', e.target.value)}
            style={{ ...inp, width: 150 }} />
        </div>
        <span style={note}>
          Keeping this on is what makes the Count column add up to the engagement&rsquo;s
          findings. A finding with no category is one nobody has classified yet &mdash;
          it is never a finding that falls outside the Top&nbsp;10.
        </span>
        <div style={row}>
          {check('showAllCategories', 'List all ten categories', config.showAllCategories === true)}
          <input value={config.noneText ?? OWASP_FINDINGS_NONE} placeholder={OWASP_FINDINGS_NONE}
            aria-label="Empty category text"
            onChange={e => update('noneText', e.target.value)}
            style={{ ...inp, width: 150 }} />
        </div>
        <span style={note}>
          Off by default. A row reading &ldquo;A01:2021 Broken Access Control &mdash; 0&rdquo;
          reads as a category that was tested and found clean, and a list of findings cannot
          tell that apart from one nobody looked at. Turn it on only where the engagement
          really did cover all ten.
        </span>
      </div>

      <div style={group}>
        <span style={cap}>Findings column</span>
        <div style={row}>
          <span style={lbl}>Name findings by:</span>
          <select value={config.findingField === 'title' ? 'title' : 'uid'}
            onChange={e => update('findingField', e.target.value)} style={{ ...inp, width: 120 }}>
            <option value="uid">Finding ID</option>
            <option value="title">Title</option>
          </select>
          <span style={lbl}>Separator:</span>
          <input value={config.separator ?? OWASP_FINDINGS_SEPARATOR}
            onChange={e => update('separator', e.target.value)} style={{ ...inp, width: 60 }} />
        </div>
      </div>

      <div style={group}>
        <span style={cap}>Severities counted</span>
        <div style={row}>
          {SEVERITIES.map(s => (
            <label key={s} style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={shownSeverities.includes(s)}
                onChange={() => toggleSeverity(s)} style={{ margin: 0, cursor: 'pointer' }} />
              {s}
            </label>
          ))}
        </div>
        <span style={note}>
          All of them unless you untick some. A filter narrows the counts as well as the
          rows, so the table stays internally consistent &mdash; but it then describes part
          of the engagement rather than all of it.
        </span>
      </div>

      <div style={group}>
        <span style={cap}>When there are no findings</span>
        <div style={row}>
          <input value={config.emptyText ?? OWASP_FINDINGS_EMPTY} placeholder={OWASP_FINDINGS_EMPTY}
            aria-label="Empty table sentence"
            onChange={e => update('emptyText', e.target.value)} style={{ ...inp, flex: 1, minWidth: 180 }} />
          <span style={lbl}>Colour:</span>{colour('emptyColor', '#5f6b7a', 'Empty sentence colour')}
        </div>
      </div>
    </div>
  );
}

export default OwaspFindingsConfig;
